import type {
  WhatsAppCallIconVisibility,
  WhatsAppEnableCallingSettings,
  WhatsAppCallbackPermissionStatus,
} from 'src/pages/chat/channel/whatsapp/call/types/whatsapp-call.types';

import { toast } from 'sonner';
import { useState } from 'react';

import {
  Card,
  Alert,
  Stack,
  Button,
  MenuItem,
  TextField,
  Typography,
  CardContent,
  CircularProgress,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { useEnableWhatsAppCallingMutation } from 'src/pages/chat/channel/whatsapp/call/slice/whatsappCallApi';
import { getWhatsAppEnableErrorMessage } from 'src/pages/chat/channel/whatsapp/call/utils/whatsapp-enable-errors';

// ----------------------------------------------------------------------

type WhatsappCallingEnableProps = {
  channelId: string;
};

export function WhatsappCallingEnable({ channelId }: WhatsappCallingEnableProps) {
  const { t } = useTranslate('translation');
  const [enableCalling, { isLoading }] = useEnableWhatsAppCallingMutation();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [callIconVisibility, setCallIconVisibility] =
    useState<WhatsAppCallIconVisibility>('DEFAULT');
  const [callbackPermissionStatus, setCallbackPermissionStatus] =
    useState<WhatsAppCallbackPermissionStatus>('ENABLED');

  const handleEnable = async () => {
    if (!channelId) {
      toast.error(t('WHATSAPP_CALLING_ENABLE_NO_CHANNEL'));
      return;
    }

    setLastError(null);

    const settings: WhatsAppEnableCallingSettings | undefined = showAdvanced
      ? { callIconVisibility, callbackPermissionStatus }
      : undefined;

    try {
      await enableCalling({ channelId, settings }).unwrap();
      toast.success(t('WHATSAPP_CALLING_ENABLE_SUCCESS'));
    } catch (error) {
      const message = getWhatsAppEnableErrorMessage(error, t);
      setLastError(message);
      toast.error(message);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('WHATSAPP_CALLING_ENABLE_TITLE')}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t('WHATSAPP_CALLING_ENABLE_DESCRIPTION')}
          </Typography>

          {lastError && (
            <Alert severity="error" onClose={() => setLastError(null)}>
              {lastError}
            </Alert>
          )}

          {showAdvanced && (
            <Stack spacing={2}>
              <TextField
                select
                size="small"
                fullWidth
                label={t('WHATSAPP_CALLING_ICON_VISIBILITY')}
                value={callIconVisibility}
                onChange={(e) =>
                  setCallIconVisibility(e.target.value as WhatsAppCallIconVisibility)
                }
              >
                <MenuItem value="DEFAULT">{t('WHATSAPP_CALLING_ICON_DEFAULT')}</MenuItem>
                <MenuItem value="DISABLE_ALL">{t('WHATSAPP_CALLING_ICON_DISABLE_ALL')}</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                fullWidth
                label={t('WHATSAPP_CALLING_CALLBACK_PERMISSION')}
                value={callbackPermissionStatus}
                onChange={(e) =>
                  setCallbackPermissionStatus(e.target.value as WhatsAppCallbackPermissionStatus)
                }
              >
                <MenuItem value="ENABLED">{t('ENABLED')}</MenuItem>
                <MenuItem value="DISABLED">{t('DISABLED')}</MenuItem>
              </TextField>
            </Stack>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Button
              variant="contained"
              color="success"
              disabled={isLoading || !channelId}
              onClick={handleEnable}
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {t('WHATSAPP_CALLING_ENABLE_BUTTON')}
            </Button>

            <Button variant="text" size="small" onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced
                ? t('WHATSAPP_CALLING_HIDE_ADVANCED')
                : t('WHATSAPP_CALLING_SHOW_ADVANCED')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
